import { I18nProviderPropsStrict } from "../../i18n/i18n";
import { FlyoutProps } from "../flyout";
import { FormLabelProps } from "../formlabel";
import { TextInputProps } from "../textinput";

// See https://en.wikipedia.org/wiki/Date_format_by_country
export const datePickerFormats = [
    // ISO 8601 variants
    "yy-mm-dd",
    "yy/mm/dd",

    // Common writing formats across many nations/locales.
    "yyyy/dd/mm",
    "yyyy-dd-mm",
    "mm/dd/yyyy",
    "mm-dd-yyyy",
    "mm/dd/yy", // most common US.
    "dd/mm/yyyy",
    "dd-mm-yyyy",
    "dd.mm.yyyy", // Finnish (Finland)
    "dd/mm/yy",
    "dd.mm.yy",
    "dd.mm.yy.", // Serbia/Montenegro/Czechia variant.

    // Hungarian variants
    "yyyy.mm.dd.",
    "yyyy.mm.dd",

    // Many Eastern European countries: Bosnia and Herzegovina, Czechia, Croatia, etc.
    "dd. mm. yyyy",

    // en-ZA English South Africa
    "yyyy/mm/dd",

    // Chinese - Hong Kong
    "yy年mm月dd日",

    // Latvia
    "yy.dd.mm",

    // Korean (North and South).
    "yy. mm. dd",

    // Spanish - PR, Nicaragua, El Salvador, Honduras.
    "mm-dd-yy",

    // Dutch - Netherlands, Portuguese, some Spanish locales.
    "dd-mm-yy",
] as const;

export const DELIMITERS = datePickerFormats
    .join("")
    .split("")
    .sort()
    .filter((item, pos, arr) => arr[pos + 1] !== arr[pos] && !"mdy".includes(arr[pos]))
    .join("");

export const DELIMITER_REGEX = new RegExp("[" + DELIMITERS + "]");

// Storing the regex for checking typed characters. This regex
// is all possible delimiter and input characters.
export const INPUT_REGEX = new RegExp("[^0-9" + DELIMITERS + "]");

/**
 * The allowed set of date string formats e.g. "mm/dd/yyyy".
 */
export type DatePickerFormat = (typeof datePickerFormats)[number];

export interface DatePickerDate {
    /**
     * The 1-indexed day of the month 1-31.
     */
    day: number;

    /**
     * The 0-indexed month of the year from 0-11.
     */
    month: number;

    /**
     * The full year i.e. yyyy.
     */
    year: number;
}

export type DatePair = [Date, Date];

export type InternalRange = null | [Date, null] | DatePair;

export type DatePickerComponentOverrideProps = {
    formLabel?: Partial<FormLabelProps>;
    textInput?: Partial<TextInputProps>;
};

/**
 * These are just the props the Date pickers need from the Flyout.
 */
export type DatePickerDialogFlyoutProps = Pick<
    FlyoutProps,
    | "aria-describedby"
    | "aria-label"
    | "aria-labelledby"
    | "className"
    | "clientId"
    | "id"
    | "isOpen"
    | "offset"
    | "placement"
    | "popperProps"
    | "portalProps"
    | "role"
    | "targetElement"
    | "updateKey"
>;

export interface SharedDatePickerDialogProps extends I18nProviderPropsStrict, DatePickerDialogFlyoutProps {
    /**
     * Function that will be called when the flyout wants to close.
     * Not using the one baked into flyout, as we need to pass an argument
     */
    onCloseRequested: (refocusTarget: boolean) => void;

    /**
     * If provided, called for each day in the current calendar viewport to check
     * if it should be selectable or not.
     *
     * Works alongside minDate/maxDate as well.
     */
    dayIsDisabled?: (date: DatePickerDate) => boolean;

    /**
     * If specified, this is the initial date that will be shown when the user opens the picker
     * dialog. If `value` is set, that date will be used instead.
     *
     * If unset, defaults to the user's locale date.
     *
     * The minDate and maxDate props will still apply to this date. If this date is outside of the
     * min/max range, the closest valid boundary date will be chosen instead.
     */
    initialPickerDate?: DatePickerDate;

    /**
     * The maximum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    maxDate?: DatePickerDate;

    /**
     * The minimum allowed date to select.
     *
     * Works together with dayIsDisabled as well.
     */
    minDate?: DatePickerDate;

    /**
     * Called when the "clear" footer button is clicked. We introduced this prop because
     * changing onChange to (newDate: DatePickerDate | null) => void would be a breaking change.
     */
    onClear?: () => void;

    /**
     * If provided, will pass the FlyoutProps to the DatePickerDialog.
     */
    portalProps?: FlyoutProps["portalProps"];
}
